import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardMedia,
  Box,
  CircularProgress,
  useTheme,
} from '@mui/material';
import Dashboard from '../dashboard';
import { fetchFile, cleanup } from './selectedVideoSlice';
import type { FilePreviewRouteParams } from './types';
import { RootState } from '../../reducers';
import Tabs from './tabs';
import Chat from './chat';
import Actions from './actions';
import VideoTitleCardHeader from './VideoTitleCardHeader';
import VideoControls from './VideoControls';
import TeamDeletionCover from '../team/TeamDeletingCover';
import VideoSkeleton from './VideoSkeleton';
import FileNotFound from './FileNotFound';
import MetaData from './MetaData';
import Trimmer from './actions/modals/Trimmer';
import Clipper from './actions/modals/Clipper';
import { useTags } from '../../hooks/useTags';
import {
  VIDEO_LEFT_COLUMN_WIDTH_KEY,
  VIDEO_CHAPTERS_VISIBLE_KEY,
} from '../../constants/tags';

const forceMediaInit = async (element: HTMLMediaElement) => {
  try {
    // Create a copy of the element to avoid mutating the parameter
    const mediaElement = element;
    // Set current time to 0 to force load
    mediaElement.currentTime = 0;
    // Load media
    await mediaElement.load();
    // Try to play and immediately pause to initialize
    const playPromise = mediaElement.play();
    if (playPromise !== undefined) {
      await playPromise;
      mediaElement.pause();
    }
  } catch (error) {
    console.log('Media initialization attempted:', error);
  }
};

const VideoPreview: React.FC = () => {
  const { fileId } = useParams<FilePreviewRouteParams>();
  const { selectedVideo } = useSelector((state: RootState) => state);
  const { file, actions, clipperVisible } = selectedVideo;
  const {
    trim: { visibility: trimmerVisibility },
  } = actions;
  const teamDeleteRequested = useSelector(
    (state: RootState) => !!state.team.selectedTeam?.deleteRequestedAt
  );
  const { url } = file || {};
  const dispatch = useDispatch();
  const theme = useTheme();
  const { getTag, setTag } = useTags();
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isAudioOnly, setIsAudioOnly] = React.useState(false);
  const [showControls, setShowControls] = React.useState(false);
  const [leftColumnWidth, setLeftColumnWidth] = React.useState(() => {
    const savedWidth = getTag(VIDEO_LEFT_COLUMN_WIDTH_KEY);
    return savedWidth ? parseFloat(savedWidth) : 66;
  });
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragStartX, setDragStartX] = React.useState(0);
  const [initialLeftColumnWidth, setInitialLeftColumnWidth] = React.useState(
    66
  );
  const [progress, setProgress] = React.useState(0);
  const [duration, setDuration] = React.useState(0);
  const isMobile =
    /Mobi|Android/i.test(navigator.userAgent) || window.innerWidth < 768;
  const [showSegments, setShowSegments] = React.useState(() => {
    const savedVisibility = getTag(VIDEO_CHAPTERS_VISIBLE_KEY);
    return savedVisibility ? savedVisibility === 'true' : true;
  });

  React.useEffect(() => {
    if (fileId) dispatch(fetchFile(fileId));
    return () => {
      dispatch(cleanup());
    };
  }, [dispatch, fileId]);

  React.useEffect(() => {
    if (file?.name) {
      document.title = `${file.name} - ScreenApp`;
    }
    return () => {
      document.title = 'ScreenApp';
    };
  }, [file?.name]);

  React.useEffect(() => {
    if (videoRef.current && file) {
      const mediaElement = videoRef.current;

      // Set initial audio-only state based on file streams
      const isAudioOnlyFile =
        file.streams?.length === 1 && file.streams[0] === 'audio';
      setIsAudioOnly(isAudioOnlyFile);

      // Force initialization
      forceMediaInit(mediaElement);

      // Set initial playing state based on element's paused property
      setIsPlaying(!mediaElement.paused);

      const handlePlay = () => {
        setIsPlaying(true);
      };

      const handlePause = () => {
        setIsPlaying(false);
      };

      const handleEnded = () => {
        setIsPlaying(false);
      };

      const handleCanPlay = () => {
        setIsLoading(false);
      };

      // Add event listeners
      mediaElement.addEventListener('play', handlePlay);
      mediaElement.addEventListener('playing', handlePlay);
      mediaElement.addEventListener('pause', handlePause);
      mediaElement.addEventListener('ended', handleEnded);
      mediaElement.addEventListener('canplay', handleCanPlay);

      return () => {
        if (mediaElement) {
          mediaElement.removeEventListener('play', handlePlay);
          mediaElement.removeEventListener('playing', handlePlay);
          mediaElement.removeEventListener('pause', handlePause);
          mediaElement.removeEventListener('ended', handleEnded);
          mediaElement.removeEventListener('canplay', handleCanPlay);
        }
      };
    }
    return undefined;
  }, [file, videoRef.current]);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (videoRef.current.paused || videoRef.current.ended) {
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            // Handle play failure
          });
        }
      } else {
        videoRef.current.pause();
      }
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStartX(e.clientX);
    setInitialLeftColumnWidth(leftColumnWidth);
  };

  const handleMouseMove = React.useCallback(
    (e: MouseEvent) => {
      if (isDragging) {
        const containerWidth = window.innerWidth;
        const dragDistance = e.clientX - dragStartX;
        const dragPercentage = (dragDistance / containerWidth) * 100;
        const newLeftColumnWidth = Math.max(
          50,
          Math.min(75, initialLeftColumnWidth + dragPercentage)
        );
        setLeftColumnWidth(newLeftColumnWidth);
        setTag({
          key: VIDEO_LEFT_COLUMN_WIDTH_KEY,
          value: newLeftColumnWidth.toString(),
        });
      }
    },
    [isDragging, dragStartX, initialLeftColumnWidth, setTag]
  );

  const handleMouseUp = React.useCallback(() => {
    setIsDragging(false);
  }, []);

  React.useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    } else {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleToggleSegments = React.useCallback(() => {
    setShowSegments((prev) => !prev);
  }, []);

  if (teamDeleteRequested) {
    return (
      <Dashboard>
        <TeamDeletionCover />
      </Dashboard>
    );
  }

  if (selectedVideo.status === 'pending' || !file) {
    return <VideoSkeleton />;
  }

  if (!isLoading && !url) {
    return <FileNotFound />;
  }

  return (
    <Dashboard>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          height: { xs: 'auto', lg: 'calc(100vh - 66px)' },
        }}
      >
        <Box
          data-testid="left-column"
          sx={{
            width: { xs: '100%', lg: `${leftColumnWidth}%` },
            height: { xs: 'auto', lg: '100%' },
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: { xs: 0, sm: 2 },
              pr: { xs: 0, sm: 2, lg: 0 },
            }}
          >
            <Card
              sx={{
                borderRadius: { xs: 0, sm: '8px' },
                position: 'relative',
                width: '100%',
                border: {
                  xs: 'none',
                  sm: `1px solid ${theme.palette.secondary.light}`,
                },
                backgroundColor: theme.palette.background.paper,
                minHeight: isAudioOnly ? '120px' : 'auto',
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  position: 'relative',
                  '&:hover': {
                    '& .video-controls': {
                      opacity: isMobile ? 1 : 1,
                    },
                  },
                }}
                onMouseEnter={() => !isMobile && setShowControls(true)}
                onMouseLeave={() => !isMobile && setShowControls(false)}
              >
                {isAudioOnly ? (
                  <Box
                    sx={{
                      height: '120px',
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: theme.palette.background.paper,
                      position: 'relative',
                    }}
                  >
                    <CardMedia
                      component="audio"
                      preload="auto"
                      src={url}
                      title={file?.name}
                      id="audio-element"
                      ref={videoRef}
                      onTimeUpdate={(e) => {
                        const audioElement = e.currentTarget as HTMLAudioElement;
                        if (audioElement?.duration) {
                          const currentProgress =
                            (audioElement.currentTime / audioElement.duration) *
                            100;
                          setProgress(currentProgress);
                        }
                      }}
                      onLoadedMetadata={(e) => {
                        const audioElement = e.currentTarget as HTMLAudioElement;
                        if (audioElement?.duration) {
                          setDuration(audioElement.duration);
                        }
                      }}
                      style={{
                        display: 'block',
                        width: '100%',
                        height: 'auto',
                      }}
                      autoPlay={false}
                      controls={false}
                    />
                    <Box
                      className="video-controls"
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        opacity: 1,
                        width: '100%',
                        zIndex: 2,
                      }}
                    >
                      <VideoControls
                        videoRef={videoRef}
                        isPlaying={isPlaying}
                        onPlayPause={handlePlayPause}
                        isAudioOnly={isAudioOnly}
                        progress={progress}
                        duration={duration}
                        file={file}
                        showSegments={showSegments}
                        onToggleSegments={handleToggleSegments}
                      />
                    </Box>
                  </Box>
                ) : (
                  <CardMedia
                    component="video"
                    preload="auto"
                    src={url}
                    title={file?.name}
                    id="video-element"
                    ref={videoRef}
                    playsInline
                    webkit-playsinline="true"
                    autoPlay
                    onClick={isMobile ? undefined : handlePlayPause}
                    controls={isMobile || document.fullscreenElement !== null}
                    onTimeUpdate={(e) => {
                      const videoElement = e.currentTarget as HTMLVideoElement;
                      if (videoElement?.duration) {
                        const currentProgress =
                          (videoElement.currentTime / videoElement.duration) *
                          100;
                        setProgress(currentProgress);
                      }
                    }}
                    onLoadedMetadata={(e) => {
                      const videoElement = e.currentTarget as HTMLVideoElement;
                      if (videoElement?.duration) {
                        setDuration(videoElement.duration);
                      }
                    }}
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block',
                      minHeight: '200px',

                      objectFit: 'contain',
                    }}
                  />
                )}

                {isLoading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: theme.palette.background.paper,
                      zIndex: 10,
                    }}
                  >
                    <CircularProgress color="secondary" size={60} />
                  </Box>
                )}

                {!isMobile && (
                  <Box
                    className="video-controls"
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      opacity: isAudioOnly || showControls ? 1 : 0,
                      transition: 'opacity 0.3s ease-in-out',
                      background: isAudioOnly
                        ? 'transparent'
                        : 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0) 100%)',
                    }}
                  >
                    <VideoControls
                      videoRef={videoRef}
                      isPlaying={isPlaying}
                      onPlayPause={handlePlayPause}
                      isAudioOnly={isAudioOnly}
                      progress={progress}
                      duration={duration}
                      file={file}
                      showSegments={showSegments}
                      onToggleSegments={handleToggleSegments}
                    />
                  </Box>
                )}
              </Box>
            </Card>
            <Box sx={{ px: { xs: 2, sm: 0 } }}>
              <VideoTitleCardHeader />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  {file && <MetaData file={file} />}
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Actions />
                  </Box>
                </Box>
                {trimmerVisibility && <Trimmer />}
                {clipperVisible && <Clipper />}
              </Box>
            </Box>
            <Card
              sx={{
                border: `1px solid ${theme.palette.secondary.light}`,
                borderRadius: { xs: 0, sm: '8px' },
                width: '100%',
                mt: 2,
              }}
            >
              <CardContent sx={{ p: '0px 0px 0px 0px !important' }}>
                <Tabs videoRef={videoRef} />
              </CardContent>
            </Card>
          </Box>
        </Box>
        <Box
          data-testid="column-divider"
          sx={{
            width: { xs: '100%', lg: theme.spacing(2) },
            height: { xs: theme.spacing(2), lg: 'auto' },
            backgroundColor: 'transparent',
            cursor: { xs: 'row-resize', lg: 'col-resize' },
            transition: 'background-color 0.3s ease',
            display: { xs: 'none', lg: 'block' },
          }}
          onMouseDown={handleMouseDown}
        />
        <Box
          data-testid="right-column"
          sx={{
            width: {
              xs: '0px',
              lg: `calc(${100 - leftColumnWidth}% - ${theme.spacing(2)})`,
            },
            height: { xs: '0px', lg: '100%' },
            overflow: 'hidden',
            display: { xs: 'none', lg: 'block' },
          }}
        >
          <Chat />
        </Box>
      </Box>
    </Dashboard>
  );
};

export default VideoPreview;
